/**
 * One caption line describing the window the page is showing — built
 * EXCLUSIVELY from values the server shipped.
 *
 * ⚠️ THIS COMPONENT IS THE COORDINATION CONTRACT WITH THE CALENDAR-DAY
 * SEMANTICS TRACK, AND ITS ZERO CLIENT DATE MATHS IS WHY IT STAYS TRUE.
 *
 * The window a wedding screen shows is resolved on the client (the pill →
 * explicit local calendar dates) and then RE-STATED by the server in
 * `range` / `range_meta`, which is what every aggregate on that response was
 * actually measured over. Those two are being brought into agreement in a
 * separate change, and the frontend and the backend do not deploy at the same
 * instant. So this caption reads the SERVER's description and nothing else: no
 * Date parsing, no timezone conversion, no arithmetic, not even a re-format.
 * Whichever side ships first, the sentence under the filter still describes the
 * numbers on the screen rather than the ones the client hoped for.
 *
 * The only transformation applied to a date is taking its leading 'YYYY-MM-DD'.
 * That is a slice, not a conversion: it works whether the server sends a plain
 * calendar date or a full ISO instant, and — unlike toLocaleDateString — it
 * cannot silently show "22 Jul" for a window the server calls 2026-07-23
 * because the operator's browser sits west of UTC. That shift is the exact
 * failure this caption exists to make impossible.
 *
 * `timezone_note` is rendered VERBATIM for the same reason: day boundaries
 * resolve in the admin backend's zone, not the operator's, and paraphrasing
 * that sentence locally is how the page comes to claim something the server
 * never said.
 *
 * TWO KEY NAMES, ONE SHAPE: /overview and /members ship this block as `range`,
 * /revenue ships it as `range_meta`. Pass whichever the endpoint used.
 *
 * No meta (an older backend, or a section that failed) renders NOTHING. A
 * caption with holes in it would be worse than no caption at all.
 */

import React from 'react';
import { Typography } from '@mui/material';
import type { RangeMeta } from './weddingApi';

interface Props {
  /** `range` on /overview and /members; `range_meta` on /revenue. */
  meta?: RangeMeta | null;
  /** Extra spacing hook for the page that places it. */
  sx?: object;
}

/** The leading calendar date of a server value. A slice — never a parse. */
function calendarDate(value: string | null | undefined): string | null {
  if (!value || typeof value !== 'string') return null;
  const head = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : value;
}

const RangeMetaCaption: React.FC<Props> = ({ meta, sx }) => {
  if (!meta) return null;

  const from = calendarDate(meta.from);
  const to = calendarDate(meta.to);
  const prevFrom = calendarDate(meta.prev_from);
  const prevTo = calendarDate(meta.prev_to);

  const parts: string[] = [];
  if (from && to) parts.push(`${from} → ${to}`);
  if (typeof meta.days === 'number') {
    parts.push(`${meta.days.toLocaleString()} ${meta.days === 1 ? 'day' : 'days'}`);
  }
  if (prevFrom && prevTo) parts.push(`compared with ${prevFrom} → ${prevTo}`);

  if (parts.length === 0 && !meta.timezone_note) return null;

  return (
    <Typography
      variant="caption"
      color="text.secondary"
      data-testid="range-meta-caption"
      sx={{ display: 'block', ...(sx || {}) }}
    >
      {parts.join(' · ')}
      {parts.length > 0 && meta.timezone_note ? '. ' : ''}
      {meta.timezone_note}
      {/*
        A period still in progress says so. Without it a partial window looks
        exactly like a completed one, and every "vs previous" delta on the page
        reads as a decline for no reason other than the day not being over.
        The server's sentence, verbatim, like the timezone note above it.
      */}
      {meta.partial && meta.partial_note ? ` ${meta.partial_note}` : ''}
    </Typography>
  );
};

export default RangeMetaCaption;
